import { type CancelToken } from 'axios';
import type { LatLngBounds } from '~/_shared/types/latLng';
import type { BoundaryTerritoryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import type { CalculateBucketFunction } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { apiPost } from '../../_shared/utils/api/api.helpers';
import { type SpreadsheetDataBulkRequestGetterFilter } from '../../spreadsheet/spreadsheet.repository';
import {
  type BoundaryTerritoryGroupCreateRequest,
  type BoundaryTerritoryResponse,
} from '../boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import { type BoundaryTerritoryGroupModel } from '../boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import {
  type BoundingBoxRequest,
  createBoundaryTerritoriesFromResponse,
  createBoundaryTerritoryGroupDetailsFromResponse,
  createBoundaryTerritoryGroupDetailsRequestQuery,
} from './boundaryTerritoryDetails.factory';
import {
  type BoundaryTerritories, type BoundaryTerritoryGroupDetails,
} from './boundaryTerritoryDetails.types';

export type BoundaryTerritoryGroupDetailsItemResponse = {
  assignments: {
    [boundary_territory_id: string]: number[];
  };
  manual_assignments: { [boundaryTerritoryId: string]: number[]};
  counts: {
    [assignmentId: number]: number;
  };
  // Extra data keyed by data_query_id then boundary_id
  // Example shape: {
  //   "1": { 123: { value: 0.1, formatted_value: "10%" },
  //   124: { value: 0.5, formatted_value: "5%" } },
  //   "2": { 123: { value: 2, formatted_value: "200%" } }
  // }
  extra_data?: {
    [data_query_id: string]: {
      [boundary_id: number]: {
        value: number | string | null;
        formatted_value?: string | null;
      };
    };
  };
  numerical_data: {
    totals: {
      [assignmentId: number]: number;
    };
    total_highest: number;
    total_lowest: number;
  };
  has_empty: boolean;
};

export type BoundaryTerritoryGroupDetailsResponse = {
  map_id: number;
  boundary_territory_group_query_id: string;
  boundary_territory_group_id: null | number;
  boundary_group_id: number;
  boundary_details: BoundaryTerritoryGroupDetailsItemResponse;
};

export type BoundaryTerritoriesResponse = {
  map_id: number;
  boundary_territory_group_query_id: string;
  boundary_territory_group_id: null | number;
  boundary_group_id: number;
  boundary_territories: BoundaryTerritoryResponse[];
};

export type BoundaryTerritoryGroupDetailsFilterRequest = Omit<SpreadsheetDataBulkRequestGetterFilter, 'only_get_filtered'>;

export type BoundaryTerritoryGroupDetailsRequestQuery = BoundaryTerritoryGroupCreateRequest & {
  boundary_territory_group_query_id: string;
  boundary_territory_group_id?: number;
  filter?: BoundaryTerritoryGroupDetailsFilterRequest;
  bounding_box?: BoundingBoxRequest;
  /**
   * Optional list of extra data queries to fetch along with the boundary territory details.
   * FE generates unique data_query_id strings to correlate request -> response.
   */
  extra_data?: Array<
    | {
      data_query_id: string; // generated on FE, used as identifier in response
      type: 'Demographic';
      demographic_id: string;
    }
    | {
      data_query_id: string; // generated on FE, used as identifier in response
      type: 'SpreadsheetColumn';
      function: CalculateBucketFunction;
      column: {
        spreadsheet_id: number;
        column_id: string;
      };
    }
  >;
};

export type BoundaryTerritoryGroupDetailsExtraDataRequest = Array<
  | {
    type: 'Demographic';
    demographicId: string;
  }
  | {
    type: 'SpreadsheetColumn';
    function: CalculateBucketFunction;
    column: {
      spreadsheetId: number;
      columnId: string;
    };
  }
>;

export type BoundaryTerritoryGroupDetailsRequestQueryModel = {
  boundaryTerritoryGroup: BoundaryTerritoryGroupModel;
  boundaryTerritoryGroupQueryId: string;
  filter: BoundaryTerritoryGroupDetailsFilterRequest | null;
  boundingBox?: LatLngBounds | null;
  extraData?: BoundaryTerritoryGroupDetailsExtraDataRequest;
};

const onlyTakePositiveId = (id?: BoundaryTerritoryGroupId): undefined | BoundaryTerritoryGroupId => {
  if (id === undefined) {
    return undefined;
  }
  return id < 0 ? undefined : id;
};

export const getBoundaryTerritoryGroupDetails = (
  clientId: number,
  query: BoundaryTerritoryGroupDetailsRequestQueryModel[],
  cancelToken?: CancelToken,
): Promise<Map<string, BoundaryTerritoryGroupDetails>> => {
  const requestUrl = `/api/clients/${clientId}/boundary-territory/boundary-details`;

  // Build each request converting FE extraData to BE extra_data while tracking mapping for response conversion.
  const extraDataMappings: { [groupQueryId: string]: { [data_query_id: string]: BoundaryTerritoryGroupDetailsExtraDataRequest[number] } } = {};
  const queryToBTGIdMap: { [groupQueryId: string]: Maybe<BoundaryTerritoryGroupId> } = {};

  const builtQueries = query.map(request => {
    const btg = request.boundaryTerritoryGroup;
    queryToBTGIdMap[request.boundaryTerritoryGroupQueryId] = btg.boundaryTerritoryGroupId;

    const buildResult = createBoundaryTerritoryGroupDetailsRequestQuery(
      {
        ...btg,
        boundaryTerritoryGroupId: onlyTakePositiveId(btg.boundaryTerritoryGroupId),
      },
      request.boundaryTerritoryGroupQueryId,
      request.filter ?? undefined,
      request.extraData,
      request.boundingBox,
    );

    if (buildResult.extraDataMapping) {
      extraDataMappings[request.boundaryTerritoryGroupQueryId] = buildResult.extraDataMapping;
    }

    return buildResult.request;
  });

  const requestParams = { query: builtQueries };

  return apiPost<{ data: BoundaryTerritoryGroupDetailsResponse[] }>(requestUrl, requestParams, { cancelToken })
    .then(response => {
      const results: Map<string, BoundaryTerritoryGroupDetails> = new Map();

      response.data.forEach(details => {
        if (!details.boundary_territory_group_id) {
          details.boundary_territory_group_id = queryToBTGIdMap[details.boundary_territory_group_query_id] ?? null;
        }

        const extraDataMapping = extraDataMappings[details.boundary_territory_group_query_id];
        results.set(details.boundary_territory_group_query_id, createBoundaryTerritoryGroupDetailsFromResponse(details, extraDataMapping));
      });

      return results;
    });
};

export const getBoundaryTerritories = (
  clientId: number,
  query: BoundaryTerritoryGroupDetailsRequestQueryModel[],
): Promise<Map<string, BoundaryTerritories>> => {
  const requestUrl = `/api/clients/${clientId}/boundary-territory/boundary-territories`;
  const queryToBTGIdMap: { [groupQueryId: string]: Maybe<BoundaryTerritoryGroupId> } = {};

  const builtQueries = query.map(request => {
    const btg = request.boundaryTerritoryGroup;
    queryToBTGIdMap[request.boundaryTerritoryGroupQueryId] = btg.boundaryTerritoryGroupId;

    const buildResult = createBoundaryTerritoryGroupDetailsRequestQuery(
      {
        ...btg,
        boundaryTerritoryGroupId: onlyTakePositiveId(btg.boundaryTerritoryGroupId),
      },
      request.boundaryTerritoryGroupQueryId,
      request.filter ?? undefined,
      request.extraData,
      request.boundingBox,
    );

    return buildResult.request;
  });

  const requestParams = { query: builtQueries };

  return apiPost<{ data: BoundaryTerritoriesResponse[] }>(requestUrl, requestParams)
    .then(response => {
      const results: Map<string, BoundaryTerritories> = new Map();

      response.data.forEach(responseData => {
        if (!responseData.boundary_territory_group_id) {
          responseData.boundary_territory_group_id = queryToBTGIdMap[responseData.boundary_territory_group_query_id] ?? null;
        }
        results.set(responseData.boundary_territory_group_query_id, createBoundaryTerritoriesFromResponse(responseData));
      });

      return results;
    });
};
